import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ScreenLoader from "components/ScreenLoader/ScreenLoader";
import { BranchAPIKey, IPolicy } from "config";
import { useAppDispatch } from "store";
import { exchangeLoginToken } from "store/actions/authentication";
import { storeAffiliateSessionData } from "utils/affiliateEstimate";
import replaceWyshwareUri from "utils/replaceWyshwareUri";

import Branch from "branch-sdk";
import { isNil } from "lodash-es";
import queryString from "query-string";

type BranchData = {
  $deeplink_path: string;
  "~feature": BranchFeature;
  policy_id?: IPolicy["id"];
  token?: string;
};

type BranchFeature =
  | "affiliate-estimate"
  | "document_category"
  | "login"
  | "new_email"
  | "policy_reinstatement"
  | "quote_review"
  | "reinstatement_quote_review"
  | "reset_password"
  | "settings_billing";

const BranchProvider: FC<{ children?: ReactNode }> = props => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const query = queryString.parse(location.search);
  const isBranchLink = !isNil(query["_branch_match_id"]);

  const [loading, setLoading] = useState(isBranchLink ? true : false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  // Navigation-completion state transition: when location reaches the path
  // queued by Branch.data, clear the loader. State-machine pattern keyed on
  // location lifecycle; cannot be derived without losing post-arrival reset.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (pendingPath && location.pathname === pendingPath) {
      setLoading(false);
      setPendingPath(null);
    }
  }, [location.pathname, pendingPath]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const redirectToPathWithToken = useCallback(
    async ({ path, token }: { path: string; token?: string }) => {
      if (token) {
        await dispatch(
          exchangeLoginToken({
            loginToken: token,
          })
        );
      }

      await navigate(path);
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    const read = () => {
      Branch.init(BranchAPIKey);

      Branch.data(async (err, data) => {
        let navigatedTo: string | null = null;

        try {
          if (err) {
            console.error(err);
            return;
          }

          if (!data || !data.data_parsed) {
            console.error("Branch Data Unavailable");
            return;
          }

          const parsedBranchData = data.data_parsed as BranchData;
          const { "~feature": branchIdentifier } = parsedBranchData;

          switch (branchIdentifier) {
            case "affiliate-estimate": {
              const target = "/products/insurance/get-wysh-estimate";
              storeAffiliateSessionData(parsedBranchData);
              navigate(target);
              navigatedTo = target;
              break;
            }

            case "login":
              navigate("/login");
              navigatedTo = "/login";
              break;

            case "new_email": {
              const token = parsedBranchData.token;
              if (token) {
                navigate(`/verify-email/${token}`);
                navigatedTo = `/verify-email/${token}`;
              }
              break;
            }

            case "policy_reinstatement":
              await redirectToPathWithToken({
                path: "/account/policy-reinstatement",
                token: parsedBranchData.token,
              });
              navigatedTo = "/account/policy-reinstatement";
              break;

            case "quote_review":
              await redirectToPathWithToken({
                path: "/fulfillment/start",
                token: parsedBranchData.token,
              });
              navigatedTo = "/fulfillment/start";
              break;

            case "reinstatement_quote_review": {
              const path = `/fulfillment/reinstatement/${parsedBranchData.policy_id}`;
              await redirectToPathWithToken({ path, token: parsedBranchData.token });
              navigatedTo = path;
              break;
            }

            case "reset_password":
              navigate(`/create-new-password/${parsedBranchData.token}`, { replace: true });
              navigatedTo = `/create-new-password/${parsedBranchData.token}`;
              break;

            case "settings_billing":
              navigate("/products/insurance/billing");
              navigatedTo = "/products/insurance/billing";
              break;

            default: {
              const redirectPath = replaceWyshwareUri(parsedBranchData["$deeplink_path"]);
              navigate(redirectPath);
              navigatedTo = redirectPath;
              break;
            }
          }
        } finally {
          if (navigatedTo) {
            setPendingPath(navigatedTo.split("?")[0]);
          } else {
            setLoading(false);
          }
        }
      });
    };

    if (isBranchLink) {
      read();
    }
  }, [isBranchLink, dispatch, navigate, redirectToPathWithToken]);

  return loading ? <ScreenLoader /> : <>{props.children}</>;
};

export default BranchProvider;
